import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { photoUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  KeyRound,
  Camera,
  AlertTriangle,
  Boxes,
  CheckCircle2,
  Loader2,
  Plus,
  Minus,
  Upload,
  X,
  MapPin,
  LogIn,
  LogOut,
  Image as ImageIcon,
} from "lucide-react";

function fmtTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  if (isNaN(h)) return t;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function PhotoThumb({ photo, onDelete }) {
  return (
    <div className="relative group">
      <div
        className="aspect-square rounded-lg bg-cover bg-center bg-muted border border-border"
        style={{ backgroundImage: `url('${photoUrl(photo.storage_path)}')` }}
      />
      <button
        onClick={() => onDelete(photo.id)}
        className="absolute -top-1.5 -right-1.5 bg-destructive text-white rounded-full p-0.5 shadow"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [uploading, setUploading] = useState("");
  const [completing, setCompleting] = useState(false);
  const fileInputs = useRef({});

  const load = () => api.get(`/jobs/${jobId}`).then((r) => setJob(r.data));
  useEffect(() => {
    load();
  }, [jobId]);

  if (!job)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );

  const photosOf = (type, item) =>
    (job.photos || []).filter(
      (p) => p.type === type && (item === undefined || p.checklist_item === item)
    );

  const upload = async (file, type, checklistItem = "", note = "") => {
    if (!file) return;
    const key = `${type}-${checklistItem}`;
    setUploading(key);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("type", type);
    fd.append("checklist_item", checklistItem);
    fd.append("note", note);
    try {
      await api.post(`/jobs/${jobId}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded");
      await load();
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading("");
    }
  };

  const deletePhoto = async (id) => {
    await api.delete(`/photos/${id}`);
    load();
  };

  const updateInv = async (item, delta) => {
    const quantity = Math.max(0, item.quantity + delta);
    await api.put(`/inventory/${item.id}`, { quantity });
    load();
  };

  const setStatus = async (status) => {
    setCompleting(true);
    try {
      await api.put(`/jobs/${jobId}`, { status });
      toast.success(status === "completed" ? "Job marked complete!" : "Status updated");
      await load();
    } finally {
      setCompleting(false);
    }
  };

  const checklist = job.property?.photo_checklist || [];
  const missing = checklist.filter((c) => photosOf("after_service", c).length === 0);
  const fmtDate = new Date(job.date + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  const FileBtn = ({ id, label, type, item = "", variant = "secondary" }) => (
    <>
      <input
        ref={(el) => (fileInputs.current[id] = el)}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          upload(e.target.files[0], type, item);
          e.target.value = "";
        }}
        data-testid={`file-input-${id}`}
      />
      <Button
        variant={variant}
        size="sm"
        onClick={() => fileInputs.current[id]?.click()}
        disabled={uploading === `${type}-${item}`}
        data-testid={`upload-${id}`}
      >
        {uploading === `${type}-${item}` ? (
          <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
        ) : (
          <Upload className="h-4 w-4 mr-1.5" />
        )}
        {label}
      </Button>
    </>
  );

  return (
    <div className="p-4 pb-28">
      <button
        onClick={() => navigate("/cleaner")}
        className="flex items-center gap-1 text-sm text-muted-foreground mb-4 mt-1"
        data-testid="back-button"
      >
        <ArrowLeft className="h-4 w-4" /> All jobs
      </button>

      {/* Header */}
      <Card className="overflow-hidden mb-4">
        <div
          className="h-32 bg-cover bg-center bg-muted"
          style={{
            backgroundImage: `url('${
              job.property_image ||
              "https://images.pexels.com/photos/28586197/pexels-photo-28586197.jpeg"
            }')`,
          }}
        />
        <div className="p-4">
          <div className="flex items-center justify-between gap-2">
            <h1 className="font-display text-xl font-semibold">{job.property_name}</h1>
            <Badge className="capitalize">{job.status.replace("_", " ")}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{fmtDate}</p>
          {job.property?.address && (
            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
              <MapPin className="h-3.5 w-3.5" /> {job.property.address}
            </p>
          )}
          {(job.property?.checkout_time || job.property?.checkin_time) && (
            <div className="grid grid-cols-2 gap-2 mt-3">
              <div className="rounded-lg bg-secondary p-2.5">
                <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <LogOut className="h-3 w-3" /> Guest checks out
                </p>
                <p className="font-display font-semibold text-primary">
                  {fmtTime(job.property?.checkout_time) || "—"}
                </p>
              </div>
              <div className="rounded-lg bg-secondary p-2.5">
                <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <LogIn className="h-3 w-3" /> Next check-in
                </p>
                <p className="font-display font-semibold text-[#DD6B20]">
                  {fmtTime(job.property?.checkin_time) || "—"}
                </p>
              </div>
            </div>
          )}
          {job.status === "scheduled" && (
            <Button
              className="w-full mt-4"
              variant="secondary"
              onClick={() => setStatus("in_progress")}
              data-testid="start-job-button"
            >
              Start cleaning
            </Button>
          )}
        </div>
      </Card>

      {/* Door codes & access */}
      <Card className="p-4 mb-4">
        <h2 className="font-display font-semibold flex items-center gap-2 mb-3">
          <KeyRound className="h-4 w-4 text-primary" /> Access &amp; door codes
        </h2>
        {(job.property?.door_codes || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No codes provided.</p>
        ) : (
          <div className="space-y-2">
            {job.property.door_codes.map((c, i) => (
              <div
                key={i}
                className="flex items-center justify-between bg-muted rounded-lg px-3 py-2.5"
              >
                <span className="text-sm">{c.label}</span>
                <span className="font-display font-bold tracking-wider text-primary">{c.code}</span>
              </div>
            ))}
          </div>
        )}
        {job.property?.access_notes && (
          <p className="text-sm text-muted-foreground mt-3 whitespace-pre-wrap">
            {job.property.access_notes}
          </p>
        )}
      </Card>

      {/* Reference photos — how it should look */}
      {(job.reference_photos || []).length > 0 && (
        <Card className="p-4 mb-4" data-testid="reference-photos-card">
          <h2 className="font-display font-semibold flex items-center gap-2 mb-1">
            <ImageIcon className="h-4 w-4 text-primary" /> How it should look
          </h2>
          <p className="text-xs text-muted-foreground mb-3">
            Reference photos from the owner — match this standard when you're done.
          </p>
          <div className="space-y-4">
            {Object.entries(
              (job.reference_photos || []).reduce((acc, p) => {
                (acc[p.room || "General"] ||= []).push(p);
                return acc;
              }, {})
            ).map(([room, photos]) => (
              <div key={room} className="space-y-1.5">
                <p className="text-sm font-medium">{room}</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {photos.map((p) => (
                    <a
                      key={p.id}
                      href={photoUrl(p.storage_path)}
                      target="_blank"
                      rel="noreferrer"
                      className="aspect-square rounded-lg bg-cover bg-center bg-muted border border-border"
                      style={{ backgroundImage: `url('${photoUrl(p.storage_path)}')` }}
                      data-testid={`reference-photo-${p.id}`}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Damage photos - before */}
      <Card className="p-4 mb-4">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[#DD6B20]" /> Damage report
          </h2>
          <FileBtn id="damage" label="Add" type="damage" variant="outline" />
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Photograph any existing damage <b>before</b> you start cleaning.
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photosOf("damage").map((p) => (
            <PhotoThumb key={p.id} photo={p} onDelete={deletePhoto} />
          ))}
          {photosOf("damage").length === 0 && (
            <p className="text-sm text-muted-foreground col-span-full py-2">
              No damage reported.
            </p>
          )}
        </div>
      </Card>

      {/* After-service checklist */}
      <Card className="p-4 mb-4">
        <h2 className="font-display font-semibold flex items-center gap-2 mb-1">
          <Camera className="h-4 w-4 text-primary" /> After-service photos
        </h2>
        <p className="text-xs text-muted-foreground mb-4">
          Required once cleaning is complete. Upload a photo for each area.
        </p>
        {checklist.length === 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              No checklist set — add general completion photos.
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-2">
              {photosOf("after_service", "").map((p) => (
                <PhotoThumb key={p.id} photo={p} onDelete={deletePhoto} />
              ))}
            </div>
            <FileBtn id="after-general" label="Add photo" type="after_service" item="" />
          </div>
        ) : (
          <div className="space-y-4">
            {checklist.map((item, idx) => {
              const ps = photosOf("after_service", item);
              const done = ps.length > 0;
              return (
                <div key={idx} className="border border-border rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium flex items-center gap-2">
                      {done ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <span className="h-4 w-4 rounded-full border-2 border-muted-foreground/40" />
                      )}
                      {item}
                    </span>
                    <FileBtn
                      id={`after-${idx}`}
                      label={done ? "Add" : "Upload"}
                      type="after_service"
                      item={item}
                      variant={done ? "outline" : "secondary"}
                    />
                  </div>
                  {ps.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {ps.map((p) => (
                        <PhotoThumb key={p.id} photo={p} onDelete={deletePhoto} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Inventory */}
      <Card className="p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-semibold flex items-center gap-2">
            <Boxes className="h-4 w-4 text-primary" /> Inventory refills
          </h2>
          <FileBtn id="inventory" label="Photo" type="inventory" variant="outline" />
        </div>
        {(job.inventory || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No items tracked for this property.</p>
        ) : (
          <div className="space-y-2">
            {job.inventory.map((it) => (
              <div
                key={it.id}
                className={`flex items-center justify-between rounded-lg px-3 py-2 ${
                  it.status === "low" ? "bg-[#FFF7ED]" : "bg-muted"
                }`}
              >
                <div>
                  <div className="text-sm font-medium flex items-center gap-2">
                    {it.name}
                    {it.status === "low" && (
                      <Badge variant="destructive" className="text-[10px]">
                        Low
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => updateInv(it, -1)}
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </Button>
                  <span className="w-6 text-center font-semibold">{it.quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => updateInv(it, 1)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {photosOf("inventory").length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
            {photosOf("inventory").map((p) => (
              <PhotoThumb key={p.id} photo={p} onDelete={deletePhoto} />
            ))}
          </div>
        )}
      </Card>

      {/* Complete */}
      {job.status !== "completed" ? (
        <div className="sticky bottom-4">
          {missing.length > 0 && (
            <p className="text-xs text-[#DD6B20] text-center mb-2 flex items-center justify-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              {missing.length} after-service photo(s) still needed
            </p>
          )}
          <Button
            className="w-full h-12 text-base shadow-lg"
            onClick={() => setStatus("completed")}
            disabled={completing || missing.length > 0}
            data-testid="complete-job-button"
          >
            {completing ? (
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="h-5 w-5 mr-2" />
            )}
            Mark job complete
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2 text-emerald-600 font-medium py-4">
          <CheckCircle2 className="h-5 w-5" /> Job completed
        </div>
      )}
    </div>
  );
}
